import { apiClient } from '@/lib/api/client';
import type { ApiEnvelope } from '@/types/api';
import type { PartnerProfile, UpdateProfileInput, PasswordChangeInput, NotificationPreferences } from '@/types/user';

export async function updateProfile(
  data: UpdateProfileInput
): Promise<ApiEnvelope<PartnerProfile>> {
  const formData = new FormData();
  if (data.name !== undefined) formData.append('name', data.name);
  if (data.organization_name !== undefined) formData.append('organization_name', data.organization_name);
  if (data.organization_name_ar !== undefined) formData.append('organization_name_ar', data.organization_name_ar);
  if (data.description !== undefined) formData.append('description', data.description);
  if (data.description_ar !== undefined) formData.append('description_ar', data.description_ar);
  if (data.email !== undefined) formData.append('email', data.email);
  if (data.website !== undefined) formData.append('website', data.website);
  if (data.avatar instanceof File) formData.append('avatar', data.avatar);

  // Must use raw fetch for multipart FormData — axios's default Content-Type: application/json
  // causes it to JSON-serialize FormData (turning File entries into {}), so we bypass it here.
  // CSRF cookie is fetched first, then the token is read and sent as X-XSRF-TOKEN header.
  await apiClient.initCsrf();
  const xsrfToken = document.cookie
    .split('; ')
    .find((row) => row.startsWith('XSRF-TOKEN='))
    ?.split('=')[1];

  const res = await fetch('/api_partner/update_profile', {
    method: 'POST',
    body: formData,
    credentials: 'include',
    headers: xsrfToken ? { 'X-XSRF-TOKEN': decodeURIComponent(xsrfToken) } : {},
  });
  return res.json() as Promise<ApiEnvelope<PartnerProfile>>;
}

export async function changePassword(
  data: PasswordChangeInput
): Promise<ApiEnvelope<null>> {
  return apiClient.post<null>('/api_partner/change_password', data);
}

/**
 * Fetch the authenticated partner's profile
 */
export async function getPartnerProfile(): Promise<ApiEnvelope<PartnerProfile>> {
  return apiClient.get<PartnerProfile>('/api_partner/partner/profile');
}

/**
 * Update the partner's profile fields (legacy wrapper)
 */
export async function updatePartnerProfile(
  data: UpdateProfileInput
): Promise<ApiEnvelope<PartnerProfile>> {
  return updateProfile(data);
}

/**
 * Upload a new partner avatar image
 */
export async function uploadPartnerAvatar(
  file: File
): Promise<ApiEnvelope<{ avatar_url: string }>> {
  const formData = new FormData();
  formData.append('avatar', file);
  return apiClient.post<{ avatar_url: string }>('/api_partner/partner/profile/avatar', formData);
}

/**
 * Fetch notification preferences
 */
export async function getNotificationPreferences(): Promise<ApiEnvelope<NotificationPreferences>> {
  return apiClient.get<NotificationPreferences>('/api_partner/partner/preferences');
}

/**
 * Update notification preferences
 */
export async function updateNotificationPreferences(
  data: Partial<NotificationPreferences>
): Promise<ApiEnvelope<NotificationPreferences>> {
  return apiClient.put<NotificationPreferences>('/api_partner/partner/preferences', data);
}

export interface CompleteProfileInput {
  first_name?: string | null;
  last_name?: string | null;
  gender?: 'male' | 'female' | null;
  date_of_birth?: string | null;
  locale?: string | null;
}

export async function completeProfile(
  input: CompleteProfileInput
): Promise<ApiEnvelope<null>> {
  return apiClient.post<null>('/api_partner/complete_profile', input);
}
